import { enableField, disableField } from '../../../actions';
import { RowItemType, Section, SelectorIdentifier, UIElementType } from '../../../models';
import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import { Mp1SensorIds, Mp1SensorTitles, Mp2SensorIds, Mp2SensorTitles, TritonSensors, getSensorFieldId } from '../base-config-models';
import { AvGatedSensorFieldIds } from './config-models';
import { MonitoringPointElement } from 'app/shared/components/location-card/models/location-card.model';
import { LocationCardSettings } from '../../../reducers';
import {
    DIRECTION_LIST_DEFAULT_VALUE,
    MaxVelocityMetricValuesNonParaFlow,
    MaxVelocityNonMetricValuesNonParaFlow,
} from 'app/shared/components/location-card/location-card.constants';
import { field } from '../../../core/field-schema';
import { pattern, required } from '../../../core/validator-builders';
import { AVGatedDevice } from '../models';

/**
 * AV|Gated sensor configuration
 */
export const avGatedSensor = (mp: 0 | 1, settings: LocationCardSettings, sensorData: AVGatedDevice): Section => {
    const isMetric = settings.isMetric;
    const maxVelocityOptions = isMetric ? MaxVelocityMetricValuesNonParaFlow : MaxVelocityNonMetricValuesNonParaFlow;

    return {
        info: {
            id: mp === 0 ? Mp1SensorIds[TritonSensors.AvGated] : Mp2SensorIds[TritonSensors.AvGated],
            title: mp === 0 ? Mp1SensorTitles[TritonSensors.AvGated] : Mp2SensorTitles[TritonSensors.AvGated],
            displayTitle: true,
            expandable: true,
            expanded: false,
        },
        rows: [
            // Flow Type & Physical Offset Row
            {
                info: {
                    id: 'flow-offset-row',
                    panelClass: 'custom-33p',
                },
                items: [
                    field.select<MonitoringPointElement, number>(getSensorFieldId(AvGatedSensorFieldIds.UltrasonicFlowType, mp), 'ultrasonicFlowType', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_FLOW',
                        value: sensorData ? sensorData.ultrasonicFlowType : 0,
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.FlowTypes,
                        panelClass: 'width-30 required',
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                    }),
                    field.number(getSensorFieldId(AvGatedSensorFieldIds.PhysicalOffset, mp), 'physicalOffset', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_PHYSICAL',
                        placeholderSuffix: settings.depthUnit,
                        value: sensorData ? sensorData.physicalOffset : 0,
                        validators: [
                            required(),
                            pattern({
                                value: isMetric
                                    ? REGEX_CONFIG.negativeWithOneDecimalPattern
                                    : REGEX_CONFIG.negativeWithTwoDecimalPattern,
                                message: isMetric ? 'COMMON.OFFSET_METRIC_ERROR' : 'COMMON.OFFSET_NON_METRIC_ERROR',
                            }),
                        ],
                        panelClass: 'width-30 required',
                    }),
                ],
            },

            // Ultrasonic Measurement Toggle Row
            {
                info: {
                    id: 'ultrasonic-enable-row',
                    panelClass: 'pad-b-15',
                },
                items: [
                    field.toggle(getSensorFieldId(AvGatedSensorFieldIds.UltrasonicEnable, mp), 'ultrasonicEnable', {
                        value: sensorData ? sensorData.ultrasonicEnable : true,
                        placeholder: 'ADD_EDIT.INACTIVE_TOGGLE_MEASUREMENT',
                        panelClass: 'custom-slide-toggle highlight-text item-flex-center',
                        events: {
                            onChange: (value) => [
                                value
                                    ? enableField({ fieldId: getSensorFieldId(AvGatedSensorFieldIds.UltrasonicElectronicOffset, mp) })
                                    : disableField({ fieldId: getSensorFieldId(AvGatedSensorFieldIds.UltrasonicElectronicOffset, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(AvGatedSensorFieldIds.UltrasonicDeadband, mp) })
                                    : disableField({ fieldId: getSensorFieldId(AvGatedSensorFieldIds.UltrasonicDeadband, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(AvGatedSensorFieldIds.UltrasonicStoreData, mp) })
                                    : disableField({ fieldId: getSensorFieldId(AvGatedSensorFieldIds.UltrasonicStoreData, mp) }),
                            ],
                        },
                    }),
                ],
            },

            // Ultrasonic Settings Row
            {
                info: {
                    id: 'ultrasonic-settings-row',
                    panelClass: 'custom-33p',
                },
                items: [
                    field.number(getSensorFieldId(AvGatedSensorFieldIds.UltrasonicElectronicOffset, mp), 'ultrasonicElectronicOffset', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_ELECTRONIC',
                        placeholderSuffix: settings.depthUnit,
                        disabled: sensorData ? !sensorData.ultrasonicEnable : false,
                        value: sensorData ? sensorData.ultrasonicElectronicOffset : 0,
                        validators: [
                            required(),
                            pattern({
                                value: REGEX_CONFIG.numericDecimal2PlacePattern,
                                message: isMetric ? 'COMMON.OFFSET_METRIC_ERROR' : 'COMMON.OFFSET_NON_METRIC_ERROR',
                            }),
                        ],
                        panelClass: 'width-30 required',
                    }),
                    field.select<MonitoringPointElement, number>(getSensorFieldId(AvGatedSensorFieldIds.UltrasonicDeadband, mp), 'ultrasonicDeadband', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_DEAD_BAND',
                        disabled: sensorData ? !sensorData.ultrasonicEnable : false,
                        value: sensorData ? sensorData.ultrasonicDeadband : 0,
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.DeadBandList,
                        panelClass: 'width-30 required',
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                    }),
                    field.toggle(getSensorFieldId(AvGatedSensorFieldIds.UltrasonicStoreData, mp), 'ultrasonicStoreData', {
                        disabled: sensorData ? !sensorData.ultrasonicEnable : false,
                        value: sensorData ? sensorData.ultrasonicStoreData : true,
                        label: 'ADD_EDIT.INACTIVE_TOGGLE_STORE_DATA',
                        panelClass: 'custom-slide-toggle item-flex-center',
                    }),
                ],
            },

            // Pressure Measurement Toggle Row
            {
                info: {
                    id: 'pressure-enable-row',
                    panelClass: 'pad-b-15',
                },
                items: [
                    field.toggle(getSensorFieldId(AvGatedSensorFieldIds.PressureEnable, mp), 'pressureEnable', {
                        value: sensorData ? sensorData.pressureEnable : true,
                        placeholder: 'ADD_EDIT.INACTIVE_TOGGLE_PRESSURE',
                        panelClass: 'custom-slide-toggle highlight-text item-flex-center',
                        events: {
                            onChange: (value) => [
                                value
                                    ? enableField({ fieldId: getSensorFieldId(AvGatedSensorFieldIds.PressureElectronicOffset, mp) })
                                    : disableField({ fieldId: getSensorFieldId(AvGatedSensorFieldIds.PressureElectronicOffset, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(AvGatedSensorFieldIds.PressureStoreData, mp) })
                                    : disableField({ fieldId: getSensorFieldId(AvGatedSensorFieldIds.PressureStoreData, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(AvGatedSensorFieldIds.PressureCalibration, mp) })
                                    : disableField({ fieldId: getSensorFieldId(AvGatedSensorFieldIds.PressureCalibration, mp) }),
                            ],
                        },
                    }),
                ],
            },

            // Pressure Settings Row
            {
                info: {
                    id: 'pressure-settings-row',
                    panelClass: 'custom-33p',
                },
                items: [
                    field.number(getSensorFieldId(AvGatedSensorFieldIds.PressureElectronicOffset, mp), 'pressureElectronicOffset', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_ELECTRONIC',
                        placeholderSuffix: settings.depthUnit,
                        disabled: sensorData ? !sensorData.pressureEnable : false,
                        value: sensorData ? sensorData.pressureElectronicOffset : 0,
                        validators: [
                            pattern({
                                value: isMetric
                                    ? REGEX_CONFIG.numericDecimal1PlacePattern
                                    : REGEX_CONFIG.numericDecimal2PlacePattern,
                                message: isMetric ? 'COMMON.OFFSET_METRIC_ERROR' : 'COMMON.OFFSET_NON_METRIC_ERROR',
                            }),
                        ],
                        panelClass: 'width-30',
                    }),
                    field.toggle(getSensorFieldId(AvGatedSensorFieldIds.PressureStoreData, mp), 'pressureStoreData', {
                        disabled: sensorData ? !sensorData.pressureEnable : false,
                        value: sensorData ? sensorData.pressureStoreData : true,
                        placeholder: 'ADD_EDIT.INACTIVE_TOGGLE_STORE_DATA',
                        panelClass: 'custom-slide-toggle item-flex-center',
                    }),
                    field.checkbox(getSensorFieldId(AvGatedSensorFieldIds.PressureCalibration, mp), 'pressureCalibration', {
                        disabled: sensorData ? !sensorData.pressureEnable : false,
                        value: sensorData ? sensorData.pressureCalibration : true,
                        placeholder: 'ADD_EDIT.INACTIVE_TOGGLE_AUTO_CALIBER',
                        panelClass: 'item-flex-center',
                    }),
                ],
            },

            // Velocity Measurement Toggle Row
            {
                info: {
                    id: 'velocity-enable-row',
                    panelClass: 'pad-b-15',
                },
                items: [
                    field.toggle(getSensorFieldId(AvGatedSensorFieldIds.VelocityEnable, mp), 'velocityEnable', {
                        value: sensorData ? sensorData.velocityEnable : true,
                        placeholder: 'ADD_EDIT.INACTIVE_TOGGLE_VELOCITY',
                        panelClass: 'custom-slide-toggle highlight-text item-flex-center',
                        events: {
                            onChange: (value) => [
                                value
                                    ? enableField({ fieldId: getSensorFieldId(AvGatedSensorFieldIds.VelocityDirection, mp) })
                                    : disableField({ fieldId: getSensorFieldId(AvGatedSensorFieldIds.VelocityDirection, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(AvGatedSensorFieldIds.VelocityMinimum, mp) })
                                    : disableField({ fieldId: getSensorFieldId(AvGatedSensorFieldIds.VelocityMinimum, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(AvGatedSensorFieldIds.VelocityMaximum, mp) })
                                    : disableField({ fieldId: getSensorFieldId(AvGatedSensorFieldIds.VelocityMaximum, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(AvGatedSensorFieldIds.VelocityAcquisition, mp) })
                                    : disableField({ fieldId: getSensorFieldId(AvGatedSensorFieldIds.VelocityAcquisition, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(AvGatedSensorFieldIds.VelocityStoreData, mp) })
                                    : disableField({ fieldId: getSensorFieldId(AvGatedSensorFieldIds.VelocityStoreData, mp) }),
                                value
                                    ? enableField({ fieldId: getSensorFieldId(AvGatedSensorFieldIds.VelocityScrubbing, mp) })
                                    : disableField({ fieldId: getSensorFieldId(AvGatedSensorFieldIds.VelocityScrubbing, mp) }),
                            ],
                        },
                    }),
                ],
            },

            // Velocity Direction & Minimum Row
            {
                info: {
                    id: 'velocity-dir-min-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<MonitoringPointElement, number>(getSensorFieldId(AvGatedSensorFieldIds.VelocityDirection, mp), 'velocityDirection', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_DIRECTION',
                        value: sensorData ? sensorData.velocityDirection : DIRECTION_LIST_DEFAULT_VALUE,
                        disabled: sensorData ? !sensorData.velocityEnable : false,
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.DirectionList,
                        panelClass: 'custom-width required',
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                    }),
                    field.select<MonitoringPointElement, number>(getSensorFieldId(AvGatedSensorFieldIds.VelocityMinimum, mp), 'velocityMinimum', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_MINIMUM_VELOCITY',
                        disabled: sensorData ? !sensorData.velocityEnable : false,
                        value: sensorData ? sensorData.velocityMinimum : (isMetric ? 0.02 : 0.05),
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.MinVelocityList,
                        panelClass: 'custom-width required',
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                    }),
                ],
            },

            // Velocity Maximum & Acquisition Row
            {
                info: {
                    id: 'velocity-max-acq-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<MonitoringPointElement, number>(getSensorFieldId(AvGatedSensorFieldIds.VelocityMaximum, mp), 'velocityMaximum', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_VALUE_MAXIMUM',
                        disabled: sensorData ? !sensorData.velocityEnable : false,
                        value: sensorData && maxVelocityOptions.find((v) => v.value === sensorData.velocityMaximum)
                            ? sensorData.velocityMaximum
                            : 0,
                        optionsSelector: SelectorIdentifier.MaxVelocity,
                        validators: [required()],
                        panelClass: 'custom-width',
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                    }),
                    field.select<MonitoringPointElement, number>(getSensorFieldId(AvGatedSensorFieldIds.VelocityAcquisition, mp), 'velocityAcquisition', {
                        disabled: sensorData ? !sensorData.velocityEnable : false,
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_DATA_ACQUISITION',
                        value: sensorData ? sensorData.velocityAcquisition : 0,
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.VelocityAcquisition,
                        panelClass: 'custom-width required',
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                    }),
                ],
            },

            // Velocity Store Data & Scrubbing Row
            {
                info: {
                    id: 'velocity-store-scrub-row',
                    panelClass: 'custom-50p pad-b-15',
                },
                items: [
                    field.toggle(getSensorFieldId(AvGatedSensorFieldIds.VelocityStoreData, mp), 'velocityStoreData', {
                        disabled: sensorData ? !sensorData.velocityEnable : false,
                        value: sensorData ? sensorData.velocityStoreData : true,
                        label: 'ADD_EDIT.INACTIVE_TOGGLE_STORE_DATA',
                        panelClass: 'custom-slide-toggle item-flex-center',
                    }),
                    field.toggle(getSensorFieldId(AvGatedSensorFieldIds.VelocityScrubbing, mp), 'velocityScrubbing', {
                        value: sensorData ? sensorData.velocityScrubbing : true,
                        label: 'ADD_EDIT.INPUT_PLACEHOLDER_SCRUBBLING',
                        disabled: sensorData ? !sensorData.velocityEnable : false,
                        panelClass: 'custom-slide-toggle item-flex-center',
                    }),
                ],
            },

            // Info Text Row
            {
                info: {
                    id: 'info-text-row',
                    panelClass: 'custom-50p text-bottom-space',
                },
                items: [
                    {
                        id: 'info-text',
                        type: RowItemType.UI,
                        uiType: UIElementType.Text,
                        name: 'infoText',
                        label: 'ADD_EDIT.ADD_EDIT_TEXT',
                        panelClass: 'form-input-bottom-space input-text font-600',
                    },
                ],
            },

            // Temperature Storage Row
            {
                info: {
                    id: 'temperature-store-row',
                    panelClass: 'pad-b-15',
                },
                items: [
                    field.toggle(getSensorFieldId(AvGatedSensorFieldIds.TemperatureStoreData, mp), 'temperatureStoreData', {
                        value: sensorData ? sensorData.temperatureStoreData : true,
                        label: 'ADD_EDIT.ACTIVE_TOGGLE_STORE',
                        panelClass: 'custom-slide-toggle item-flex-center',
                    }),
                ],
            },

            // Hidden passthrough fields (populated from API, not displayed)
            {
                info: {
                    id: 'av-gated-hidden-fields-row',
                },
                items: [
                    field.number(getSensorFieldId(AvGatedSensorFieldIds.VelocityGain, mp), 'velocityGain', {
                        value: sensorData ? sensorData.velocityGain : 0,
                        visible: false,
                    }),
                    field.number(getSensorFieldId(AvGatedSensorFieldIds.VelocitySensitivity, mp), 'velocitySensitivity', {
                        value: sensorData ? sensorData.velocitySensitivity : 0,
                        visible: false,
                    }),
                    field.number(getSensorFieldId(AvGatedSensorFieldIds.VelocityDacGain, mp), 'velocityDacGain', {
                        value: sensorData ? sensorData.velocityDacGain : 0,
                        visible: false,
                    }),
                    field.number(getSensorFieldId(AvGatedSensorFieldIds.VelocityPingAmplitude, mp), 'velocityPingAmplitude', {
                        value: sensorData ? sensorData.velocityPingAmplitude : 0,
                        visible: false,
                    }),
                    field.number(getSensorFieldId(AvGatedSensorFieldIds.VelocityFrequency, mp), 'velocityFrequency', {
                        value: sensorData ? sensorData.velocityFrequency : 0,
                        visible: false,
                    }),
                    field.number(getSensorFieldId(AvGatedSensorFieldIds.UltrasonicDacGain, mp), 'ultrasonicDacGain', {
                        value: sensorData ? sensorData.ultrasonicDacGain : 0,
                        visible: false,
                    }),
                    field.number(getSensorFieldId(AvGatedSensorFieldIds.UltrasonicPingAmplitude, mp), 'ultrasonicPingAmplitude', {
                        value: sensorData ? sensorData.ultrasonicPingAmplitude : 0,
                        visible: false,
                    }),
                ],
            },
        ],
    };
};
